/**
 * Superadmin shell (ADR-0036 Phase 2) — the separate control-plane chrome.
 *
 * Gates on the platform token; renders a red-accented header + a left nav rail +
 * the active page via <Outlet>. Deliberately distinct from the operator AppShell
 * (a different realm should LOOK different). Reachable at `/platform` (a
 * dedicated subdomain can point its root here in prod; path-based works in dev).
 */
import {
  Briefcase,
  Building2,
  KeySquare,
  Server,
  CreditCard,
  Gauge,
  Handshake,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  ScrollText,
  ShieldCheck,
  UserPlus,
  Users,
  type LucideIcon,
} from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Navigate, Outlet } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";
import { endPlatformSession, platformEnvLabel, usePlatformAuth } from "@/lib/platform";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}

/** A nav group — an optional `title` (a small caps header on the expanded rail;
 * a divider only on the collapsed icon rail) over its items. The control-plane
 * nav is GROUPED by concern: the home, the organization-facing entities (tenants +
 * their plans), and the platform-governance surfaces. A new superadmin surface
 * slots into the matching group, not a flat append. */
const NAV_SECTIONS: { title?: string; items: NavItem[] }[] = [
  { items: [{ to: "/platform", label: "Dashboard", icon: LayoutDashboard, end: true }] },
  {
    title: "Organizzazioni",
    items: [
      // The commercial anagrafica FIRST (ADR-0074 "modello Organizzazione"): a Organizzazione is the
      // company, a Tenant is one workspace it bought — so the company comes before the
      // deployments it owns, and the two words stop meaning the same thing.
      { to: "/platform/organizations", label: "Organizzazioni", icon: Briefcase },
      { to: "/platform/tenants", label: "Tenant", icon: Building2 },
      // The identity console (ADR-0077 S4): PEOPLE across workspaces — the
      // offboarding surface. Next to Tenant because a person is the other axis
      // of the same organization graph.
      { to: "/platform/identities", label: "Account Tray", icon: Users },
      { to: "/platform/plans", label: "Piani", icon: CreditCard },
      { to: "/platform/licenses", label: "Licenze", icon: KeySquare },
      { to: "/platform/onboarding", label: "Registrazioni", icon: UserPlus },
      // I rivenditori (ADR-0081) stanno in questo gruppo e non in "Sistema": un partner
      // è un'anagrafica commerciale che TIENE delle Organizzazioni, non un pezzo di
      // infrastruttura — sta accanto a chi tiene, non accanto alle celle.
      { to: "/platform/partners", label: "Partner", icon: Handshake },
    ],
  },
  {
    title: "Sistema",
    items: [
      { to: "/platform/cells", label: "Celle", icon: Server },
      { to: "/platform/admins", label: "Amministratori", icon: Users },
      // L'assistenza sta in "Sistema" accanto agli amministratori perché è l'altra metà
      // della stessa domanda — chi di NOI entra, e con che cappello — ma NON è lo stesso
      // potere: un superadmin governa la flotta, chi fa assistenza serve il cliente
      // attraverso la coda dei ticket, e non conia né provisiona niente (ADR-0082 D4).
      // "Staff", not "Assistenza": the page manages WHO wears the staff hat (the arcs),
      // while the support work itself lives on Central — the owner searched for "staff"
      // and could not find this entry under the old name.
      { to: "/platform/staff", label: "Staff", icon: LifeBuoy },
      { to: "/platform/audit", label: "Audit", icon: ScrollText },
      { to: "/platform/security", label: "Sicurezza", icon: ShieldCheck },
      { to: "/platform/settings", label: "Impostazioni", icon: Gauge },
    ],
  },
];

export function PlatformShell() {
  const { token, admin } = usePlatformAuth();
  const { i18n } = useTranslation();

  // The control plane is authored entirely in Italian (its labels are hardcoded);
  // a superadmin has no per-user language preference to sync, so pin the locale to
  // `it` here instead of inheriting the browser/operator default (which left billing
  // axis labels in English). Not persisted — the operator app re-applies its own pref.
  React.useEffect(() => {
    if (i18n.language?.slice(0, 2) !== "it") void i18n.changeLanguage("it");
  }, [i18n]);

  if (!token) return <Navigate to="/platform/login" replace />;

  const env = platformEnvLabel();
  const adminName = admin?.full_name || admin?.email || "Superadmin";

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <header className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-border/60 bg-surface px-4 py-3 sm:px-6">
        <div className="flex min-w-0 items-center gap-2">
          <img
            src="/tray-logo.svg"
            alt=""
            aria-hidden="true"
            width={24}
            height={24}
            className="h-6 w-6 shrink-0"
          />
          <span className="truncate font-semibold">Tray · Superadmin</span>
          <span className="ml-2 hidden rounded bg-danger/10 px-1.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-danger sm:inline">
            control plane
          </span>
          <span
            className={cn(
              "ml-1 hidden rounded px-1.5 py-0.5 text-[11px] font-medium uppercase tracking-wide sm:inline",
              env === "PROD" ? "bg-warning/15 text-warning" : "bg-muted text-muted-foreground",
            )}
            title={env === "PROD" ? "Ambiente di produzione" : "Ambiente di sviluppo"}
          >
            {env}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-3 text-sm text-muted-foreground">
          <span className="max-w-[16rem] truncate" title={admin?.email ?? undefined}>
            {adminName}
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void endPlatformSession()}
            aria-label="Esci"
          >
            <LogOut className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <nav className="w-14 shrink-0 border-r border-border/60 bg-surface p-2 lg:w-52 lg:p-3">
          {NAV_SECTIONS.map((section, i) => (
            <div
              key={section.title ?? "home"}
              className={cn(
                i > 0 && "mt-1 lg:mt-4",
                // Collapsed rail has no headers — a hairline keeps the groups legible.
                i > 0 && "border-t border-border/60 pt-2 lg:border-t-0 lg:pt-0",
              )}
            >
              {section.title && (
                <div className="hidden px-3 pb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70 lg:block">
                  {section.title}
                </div>
              )}
              <ul className="space-y-1">
                {section.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      title={item.label}
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors lg:px-3",
                          "justify-center lg:justify-start",
                          isActive
                            ? "bg-accent/10 font-medium text-foreground"
                            : "text-muted-foreground hover:bg-muted hover:text-foreground",
                        )
                      }
                    >
                      <item.icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                      <span className="hidden lg:inline">{item.label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
